import { Injectable } from '@angular/core';
import { TransactionsState } from '../core/state/transactions.state';
import { Observable, Subject } from 'rxjs';
import { Transaction } from '../model/transaction';
import { TransactionsStoreService } from '../core/services/fire-store/transactions-store.service';
import { takeUntil, tap, take } from 'rxjs/operators';
import { PtransactionsStoreService } from '../core/services/fire-store/ptransactions-store.service';
import { Ptransaction } from '../model/ptransaction';
import { PeriodicTransactionsManager } from '../core/functions/periodic-transactions-manager.service';
import { CloudFunctions } from '../core/services/cloud-functions/cloud-functions.service';

@Injectable()
export class TransactionsFacade {
  private destroyed$ = new Subject();
  constructor(
    private readonly cloudFunctions: CloudFunctions,
    private readonly ptManager: PeriodicTransactionsManager,
    private readonly ptransactionsAPI: PtransactionsStoreService,
    private readonly transactionsAPI: TransactionsStoreService,
    private readonly transactionsState: TransactionsState
  ) {}

  isUpdating$(): Observable<boolean> {
    return this.transactionsState.isUpdating$();
  }

  getTransactions$(): Observable<Transaction[]> {
    return this.transactionsState.getTransactions$();
  }

  getPTransactions$(): Observable<Ptransaction[]> {
    return this.transactionsState.getPTransactions$();
  }

  getPrediction$(): Observable<any[]> {
    return this.transactionsState.getPrediction$();
  }

  loadData() {
    this.loadTransactions();
    this.loadPTransactions();
  }

  loadTransactions() {
    return this.transactionsAPI
      .getAllTransactions()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (transactions) => this.transactionsState.setTransactions$(transactions),
        (error) => console.log(error)
      );
  }

  loadPTransactions() {
    return this.ptransactionsAPI
      .getAllPTransactions()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (ptransactions) => {
          ptransactions.forEach((ptransaction) =>
            this.managePeriodicTransactions(ptransaction)
          );
          this.transactionsState.setPTransactions$(ptransactions);
        },
        (error) => console.log(error)
      );
  }

  stopSubscription() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // Optimistic update
  // 1. update UI state
  // 2. call API
  addTransaction(transaction: Transaction) {
    this.transactionsState.addTransaction(transaction);

    // Upload transaction without id
    const transactionWithoutId = JSON.parse(JSON.stringify(transaction));
    delete transactionWithoutId.id;
    this.transactionsAPI.addTransaction(transactionWithoutId).then(
      (docRef) => {
        // An id has been generated by the server, let's update the state
        const transactionWithNewId = JSON.parse(JSON.stringify(transaction));
        transactionWithNewId.id = docRef.id;
        this.transactionsState.updateTransactionId(
          transaction,
          transactionWithNewId
        );
      },
      (error: any) => {
        // error callback - we need to rollback the state change
        this.transactionsState.removeTransaction(transaction);
        console.log(error);
      }
    );
  }

  addPTransaction(ptransaction: Ptransaction) {
    this.transactionsState.addPTransaction(ptransaction);
    const ptransactionWithoutId = JSON.parse(JSON.stringify(ptransaction));
    delete ptransactionWithoutId.id;
    this.ptransactionsAPI.addPTransaction(ptransactionWithoutId).then(
      (docRef) => {
        const addedPTransactionWithId = JSON.parse(
          JSON.stringify(ptransaction)
        );
        addedPTransactionWithId.id = docRef.id;

        // // It generates standard transactions if needed.
        // this.managePeriodicTransactions(addedPTransactionWithId);

        // Update the periodicTransaction
        this.transactionsState.updatePTransactionId(
          ptransaction,
          addedPTransactionWithId
        );
      },
      (error: any) => {
        // error callback - we need to rollback the state change
        console.log('Error');

        this.transactionsState.removePTransaction(ptransaction);
        console.log(error);
      }
    );
  }

  managePeriodicTransactions(addedPTransactionWithId) {
    if (this.ptManager.needsAddStaticTransactions(addedPTransactionWithId)) {
      const now = new Date();
      const today = `${
        now.getMonth() + 1
      }/${now.getDate()}/${now.getFullYear()}`;

      const generateUntil =
        addedPTransactionWithId.endAt > today
          ? addedPTransactionWithId.endAt
          : now.getTime();

      const oldTransactions = this.ptManager.generateTransactions(
        addedPTransactionWithId,
        generateUntil
      );
      debugger;

      addedPTransactionWithId.updated =
        oldTransactions.length > 0
          ? this.ptManager
              .calculateNext(
                addedPTransactionWithId.each,
                oldTransactions[oldTransactions.length - 1].date
              )
              .getTime()
          : addedPTransactionWithId.startAt;

      debugger;
      // this.transactionsState.addTransactions(oldTransactions);
      oldTransactions.forEach((transaction) => delete transaction.id);
      this.transactionsAPI
        .addTransactions(oldTransactions)
        .then(() =>
          this.ptransactionsAPI.updatePTransaction(addedPTransactionWithId)
        );
    } else console.log('Its already up to date', addedPTransactionWithId);
  }

  // pessimistic update
  // 1. call API
  // 2. update UI state
  updateTransaction(transaction: Transaction) {
    console.log('updating transaction', transaction);

    this.transactionsState.setUpdating(true);
    this.transactionsAPI
      .updateTransaction(transaction)
      .then(() => this.transactionsState.updateTransaction(transaction))
      .catch((error) => console.log(console.log(error)))
      .finally(() => this.transactionsState.setUpdating(false));
  }

  updateTransactions(transactions: Transaction[]) {
    console.log('updating transactions', transactions);

    this.transactionsState.setUpdating(true);
    return this.transactionsAPI
      .updateTransactions(transactions)
      .then(() => this.transactionsState.updateTransactions(transactions))
      .catch((error) => console.log(console.log(error)))
      .finally(() => this.transactionsState.setUpdating(false));
  }

  editPTransaction(ptransaction: Ptransaction, transactions: Transaction[]) {
    this.clearPeriodicTransaction(ptransaction, transactions).then(() => {
      this.addPTransaction(ptransaction);
    });
  }

  updatePTransaction(ptransaction: Ptransaction) {
    this.transactionsState.setUpdating(true);
    this.ptransactionsAPI
      .updatePTransaction(ptransaction)
      .then(() => this.transactionsState.updatePTransaction(ptransaction))
      .catch((error) => console.log(console.log(error)))
      .finally(() => this.transactionsState.setUpdating(false));
  }

  clearPeriodicTransaction(
    ptransaction: Ptransaction,
    transactions: Transaction[]
  ) {
    const transactionsToDelete = this.ptManager.getTransactions(
      ptransaction,
      transactions
    );
    return this.deleteTransactions(transactionsToDelete).then(() =>
      this.deletePTransaction(ptransaction)
    );
  }
  // Optimistic update
  // 1. update UI state
  // 2. call API

  deleteTransaction(transaction: Transaction) {
    this.transactionsState.removeTransaction(transaction);
    this.transactionsAPI
      .deleteTransaction(transaction)
      .then((data) => {
        console.log(data);
      })
      .catch((error: any) => {
        // error callback - we need to rollback the state change
        this.transactionsState.addTransaction(transaction);
        console.log(error);
      });
  }

  deleteTransactions(transactions: Transaction[]) {
    this.transactionsState.removeTransactions(transactions);
    return this.transactionsAPI
      .deleteTransactions(transactions)
      .then((data) => {
        console.log(data);
      })
      .catch((error: any) => {
        // error callback - we need to rollback the state change
        this.transactionsState.addTransactions(transactions);
        console.log(error);
      });
  }

  deletePTransaction(ptransaction: Ptransaction) {
    this.transactionsState.removePTransaction(ptransaction);

    return this.ptransactionsAPI
      .deletePTransaction(ptransaction)
      .then((data) => {
        console.log(data);
      })
      .catch((error: any) => {
        // error callback - we need to rollback the state change
        this.transactionsState.addPTransaction(ptransaction);
        console.log(error);
      });
  }

  simulateFutureTransactions(ptransactions: Ptransaction[]) {
    ptransactions.forEach((ptransaction) => {
      const futureTransactions = this.ptManager.generateTransactions(
        ptransaction,
        ptransaction.endAt
      );
      this.transactionsState.addTransactions(futureTransactions);
    });
  }

  exitSimulationOfFutureTransactions(transactions: Transaction[]) {
    this.transactionsState.setTransactions$(transactions);
  }

  calculatePredictions(instances: number[]) {
    this.cloudFunctions.predictSavings(instances).subscribe(
      (predictions) => this.transactionsState.setPrediction$(predictions),
      (error) => console.log('Error getting predicctions: ', error)
    );
  }
}
